// src/tabs/logements/LivretModulesEditor.jsx — Phase A.7.1b (ADR-120)
//
// Container principal sous-onglet '📦 Modules' dans LivretTab.
//
// Pattern :
//   1. Mount : fetch livret_admin_data → {config, modules[]}
//   2. step 'loading' / 'no_config' / 'ready' / 'error'
//   3. Layout :
//      [LivretConfigEditor]
//      [LivretPublishToggle | Bouton Aperçu]
//      [Toggle FR | EN]
//      [DndContext global wrapping LivretCategoryColumn × N]  flex-wrap grid
//
// Phase A.7.1b refactor F-CRIT-3 :
//   - 1 seul DndContext global (au lieu de N par colonne en A.4)
//   - state optimiste localGrouped + isDraggingRef au niveau PARENT
//   - onDragStart/End/Cancel centralisés
//   - Q-D Dimitri Option B : drag intra-catégorie uniquement (inter-catégorie
//     rejeté dans handleDragEnd via reorderInCategory)
//
// Pessimist M-4 : localStorage key admin DISTINCTE voyageur
//   (fbz_livret_admin_lang vs fbz_livret_lang)

import { useState, useEffect, useCallback, useRef } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { DS } from "../../lib/ui";
import {
  Box, Sparkles, HelpCircle, Rss, Languages, Download,
  Rocket, AlertTriangle, Plus, ChevronDown, Palette, Smartphone,
  Maximize2, RotateCcw,
} from "lucide-react";
import { supabase } from "../../supabase";
import LivretCategoryColumn from "./LivretCategoryColumn";
import LivretModuleEditor from "./LivretModuleEditor";
import LivretConfigEditor from "./LivretConfigEditor";
import LivretPublishToggle from "./LivretPublishToggle";
import LivretPreviewPanel from "./LivretPreviewPanel";
import LivretQrDownloadButton from "./LivretQrDownloadButton";
import LivretCopyFromModal from "./LivretCopyFromModal";
import {
  autoTranslateEmptyLangs,
  retranslateAllLangs,
} from "../../lib/livret-autotranslate";
// M7 — modale de confirmation avant re-traduction avec écrasement.
import RetranslateConfirmModal from "./RetranslateConfirmModal";
import { getAllLogsFlat } from "../../lib/sousloc-helpers";
// Phase A.7.1b — 5 nouveaux composants admin tabs internes
// Sprint A.9.3 PR-A93-1f (ADR-149) : LivretIdentityEditor supprimé (charte FBZ
// uniforme navy/sky/terracotta — palette 8 couleurs + logo upload par logement
// abandonnés). L7 : LivretAdvancedConfigEditor supprimé (fuseau = champ mort,
// langues figées) — onglet « infos » = LivretInfosEditor seul.
// Galerie déplacée au niveau logement (Option B step 1) : l'édition est dans
// l'onglet « Info FBZ » (LogementGalleryEditor). Le livret reste consommateur.
import LivretFaqEditor from "./LivretFaqEditor";
import LivretInfosEditor from "./LivretInfosEditor";
// Sprint A.9.4 PR-A93-2 (ADR-151) — Sections auto IA livret (3 sections : assistance/alentours/marches)
import LivretSectionsAutoEditor from "./LivretSectionsAutoEditor";
// PR-FIX-5d C3 (ADR-163 D6) — CRUD admin catégories dynamiques livret_categories.
import CreateCategoryModal from "./CreateCategoryModal";
import {
  groupByCategory,
  reorderInCategory,
  LIVRET_CATEGORY_ORDER,
} from "../../lib/livret-editor-utils";

// M-4 (pessimist) : key distincte du voyageur (PublicLivretPage = 'fbz_livret_lang')
const ADMIN_LANG_STORAGE_KEY = "fbz_livret_admin_lang";

// ── Rattrapage traduction (réutilise lib/livret-autotranslate) ─────────────
// Construit le jsonb final {fr, en?, es?} : FR préservé + valeurs renvoyées par
// le helper (les cibles non vides y sont retournées telles quelles → jamais
// d'écrasement). `tr` = { en, es } issu de autoTranslateEmptyLangs.
export function buildLangPatch(orig, tr) {
  const fr = (orig?.fr || "").trim();
  const out = {};
  if (fr) out.fr = fr;
  if (tr.en) out.en = tr.en;
  if (tr.es) out.es = tr.es;
  return out;
}
// Vrai si le rattrapage a produit une NOUVELLE valeur (cible auparavant vide).
// M7 : sert aussi au mode overwrite — si la re-traduction renvoie une valeur
// identique à l'existant (échec ⇒ existant conservé, OU cache chaud ⇒ même
// traduction), langChanged reste false ⇒ 0 PATCH pour ce champ.
export function langChanged(orig, tr) {
  return (
    tr.en !== (orig?.en || "").trim() || tr.es !== (orig?.es || "").trim()
  );
}

// M7 — Compte des champs EN/ES NON VIDES qui seront écrasés par la passe
// overwrite (condition pessimist B3 : ce compte s'affiche DANS la modale de
// confirmation). Parcours 100% LOCAL des données déjà chargées (modules + FAQ
// + config) — aucun appel réseau. Un champ dont le FR est vide n'est JAMAIS
// re-traduit (garde fr-vide du helper) → exclu du compte.
export function countOverwritableLangs(modules, faqs, config) {
  const fields = [];
  for (const m of modules || []) fields.push(m?.title, m?.content);
  for (const f of faqs || []) fields.push(f?.question, f?.answer);
  if (config) fields.push(config.title, config.welcome_subtitle);
  let n = 0;
  for (const fld of fields) {
    if (!(fld?.fr || "").trim()) continue;
    if ((fld?.en || "").trim()) n += 1;
    if ((fld?.es || "").trim()) n += 1;
  }
  return n;
}

export default function LivretModulesEditor({ log, cfg }) {
  const logement_id = log?.id;

  const [step, setStep] = useState("loading");
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [modules, setModules] = useState([]);
  // ADR-171 — catégories dynamiques (livret_categories DB).
  // F-CRIT-2 pessimist : state explicite alimenté par loadData.
  const [categories, setCategories] = useState([]);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [categoryBusyId, setCategoryBusyId] = useState(null);
  const [creatingCategoryModal, setCreatingCategoryModal] = useState(false);
  // Trou C pessimist — annonce a11y save inline catégorie (lecteur d'écran)
  const [categoryAnnounce, setCategoryAnnounce] = useState("");
  // Phase A.7.1b : enrichissement data (faqs + module_photos).
  // Galerie déplacée au niveau logement (Option B step 1) — plus d'état ici.
  const [faqs, setFaqs] = useState([]);
  // Sprint A.9.1 PR-A91-2 (ADR-128) — F-CRIT pessimist mitigation :
  // state modulePhotos = { module_id: [photo, ...] } populé via loadData.
  // Permet passer currentPhoto au <LivretModuleEditor> à l'ouverture.
  const [modulePhotos, setModulePhotos] = useState({});
  // L3 (maquette #3) — accordéons tintés de l'éditeur. Remplace les anciens
  // sous-onglets (subTab) par des sections repliables indépendantes. État UI
  // pur (aucune donnée) : chaque clé pilote l'ouverture/fermeture d'un accordéon.
  // Le contenu d'une section n'est monté que lorsqu'elle est ouverte — miroir
  // exact de l'ancien rendu conditionnel par sous-onglet (mount à l'ouverture).
  const [openSections, setOpenSections] = useState(() => ({
    apparence: true,
    modules: true,
    sections_auto: false,
    faq: false,
    infos: false,
  }));
  const toggleSection = useCallback((key) => {
    setOpenSections((s) => ({ ...s, [key]: !s[key] }));
  }, []);
  const [activeLang, setActiveLang] = useState(() => {
    try {
      return localStorage.getItem(ADMIN_LANG_STORAGE_KEY) || "fr";
    } catch (_) {
      return "fr";
    }
  });
  // Rattrapage traduction par logement (modules + FAQ + config).
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState(null);
  // M7 — modale « Re-traduire tout » : null = fermée, sinon compte des champs
  // EN/ES non vides qui seront écrasés (calculé LOCALEMENT à l'ouverture —
  // données déjà chargées, 0 appel réseau — et affiché DANS la modale, B3).
  const [retranslateCount, setRetranslateCount] = useState(null);
  // L3 — modal « Récupérer depuis [logement] » : null | "modules" | "faqs".
  const [copyModalMode, setCopyModalMode] = useState(null);
  // Logements candidats comme source (tous sauf le courant, dédup byId).
  const copyCandidates = (getAllLogsFlat(cfg) || [])
    .filter((l) => l && l.id && l.id !== logement_id)
    .map((l) => ({ id: l.id, nom: l.nom || l.id }));

  // Modals state
  const [editingModule, setEditingModule] = useState(null);
  const [creatingCategory, setCreatingCategory] = useState(null);

  // Preview state
  // previewUrl = token-URL /livret/r/:token construit PROACTIVEMENT (cf. effet
  // ci-dessous) et partagé par l'aperçu INLINE (colonne droite) ET le plein
  // écran (overlay). fullscreenOpen pilote uniquement l'overlay.
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // B6 — erreur DÉDIÉE à l'aperçu : en step "ready" l'erreur globale `error`
  // n'est rendue que dans le bandeau au-dessus des colonnes, mais l'échec du
  // token d'aperçu y était invisible en pratique (catch → setError écrasé par
  // les resets des autres handlers). previewError est rendue DANS le
  // placeholder d'aperçu (colonne droite) avec un bouton « Réessayer ».
  const [previewError, setPreviewError] = useState(null);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  // ── F-CRIT-3 — DnD state optimiste centralisé ─────────────────────────
  //
  // localGrouped = state optimiste { category: modules[] } (drag-friendly).
  // isDraggingRef = guard anti-resync prop pendant drag actif (M-3 acté A.4).
  //
  // Pattern :
  //   - Sync localGrouped depuis modules prop quand !isDraggingRef.current
  //   - onDragStart : isDraggingRef.current = true
  //   - onDragEnd : reorder LOCAL (optimistic) + PATCH backend async
  //   - onDragCancel : isDraggingRef.current = false (resync prochain modules update)
  const [localGrouped, setLocalGrouped] = useState(() => groupByCategory([]));
  const isDraggingRef = useRef(false);

  // ADR-171 (F-CRIT-1 pessimist + Trou A) — categoryOrder dynamique dérivé
  // de `categories` state. Fallback statique LIVRET_CATEGORY_ORDER si vide.
  const categoryOrder =
    categories.length > 0 ? categories.map((c) => c.slug) : LIVRET_CATEGORY_ORDER;

  // Re-sync localGrouped quand modules OU categories change (sauf pendant drag actif).
  // F-CRIT-2 pessimist : `[modules, categories]` deps explicites — sans `categories`,
  // un renommage de catégorie ne re-grouperait pas (header reste à l'ancien label).
  useEffect(() => {
    if (!isDraggingRef.current) {
      setLocalGrouped(groupByCategory(modules, categoryOrder));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules, categories]);

  // Sensors centralisés (cohérent avec A.4 : PointerSensor distance 8px + KeyboardSensor)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Bandeau prod warning (cf. CLAUDE.md stratégie envs)
  const isLocalhost =
    typeof window !== "undefined" && window.location.hostname === "localhost";

  // Helper auth headers (récupère JWT supabase)
  const getAuthHeaders = useCallback(async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const token = session?.access_token;
    return token
      ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
      : { "Content-Type": "application/json" };
  }, []);

  // ── Fetch admin data ──────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!logement_id) return;
    setStep("loading");
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `/api/beds24-mapping?action=livret_admin_data&logement_id=${encodeURIComponent(logement_id)}`,
        { method: "GET", headers },
      );
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json.message || json.error || `HTTP ${resp.status}`);
      }
      setConfig(json.config || null);
      setModules(json.modules || []);
      // Galerie déplacée au niveau logement (json.gallery non consommé ici).
      setFaqs(json.faqs || []);
      // ADR-171 — catégories dynamiques (livret_categories DB enrichi handler)
      setCategories(Array.isArray(json.categories) ? json.categories : []);
      // Sprint A.9.1 PR-A91-2 (ADR-128) : module_photos dict
      // { module_id: [{id, public_url, storage_path, ...}, ...] }
      setModulePhotos(json.module_photos || {});
      setStep(json.config ? "ready" : "no_config");
    } catch (e) {
      setError(e.message || String(e));
      setStep("error");
    }
  }, [logement_id, getAuthHeaders]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Preview token-URL (réutilisé inline + plein écran) ────────────────────
  // Construit/réutilise le token form_tokens admin permanent (idempotent côté
  // serveur) → URL /livret/r/:token (rendu voyageur réel, gelé ADR-149).
  const fetchPreviewUrl = useCallback(async () => {
    setPreviewLoading(true);
    // B6 — reset de l'erreur d'aperçu en début de tentative (PAS setError
    // global : l'échec d'aperçu est un problème local à la colonne droite).
    setPreviewError(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `/api/beds24-mapping?action=livret_admin_preview_token`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            action: "livret_admin_preview_token",
            logement_id,
          }),
        },
      );
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json.message || json.error || `HTTP ${resp.status}`);
      }
      setPreviewUrl(json.livret_url);
      return json.livret_url;
    } catch (e) {
      // B6 — erreur locale d'aperçu (jamais setError global, cf. ci-dessus).
      setPreviewError(e.message || String(e));
      return null;
    } finally {
      setPreviewLoading(false);
    }
  }, [getAuthHeaders, logement_id]);

  // Aperçu INLINE (correction L3) : construit le token-URL PROACTIVEMENT dès que
  // le livret est prêt ET publié, sans attendre le clic « Plein écran ». La route
  // /livret/r/:token ne rend le contenu voyageur que si le livret est publié
  // (cf. handleLivretAdminPreviewToken) → on n'amorce l'iframe qu'à ce moment ;
  // sinon la colonne droite affiche un placeholder. Guard anti-boucle : pas de
  // re-fetch si déjà chargé ou en cours.
  useEffect(() => {
    if (
      step === "ready" &&
      config?.published === true &&
      !previewUrl &&
      !previewLoading
    ) {
      fetchPreviewUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, config?.published]);

  // ── Save active lang dans localStorage ────────────────────────────────
  function handleSetLang(next) {
    setActiveLang(next);
    try {
      localStorage.setItem(ADMIN_LANG_STORAGE_KEY, next);
    } catch (_) {
      /* RGPD ignoré */
    }
  }

  // ── Rattrapage : traduire tout ce livret (modules + FAQ + config) ─────────
  // Parcourt modules (titre+contenu) + FAQ (question+réponse) + config
  // (titre+welcome). PATCH partiels via les endpoints existants.
  //
  // M7 — la boucle est factorisée dans runTranslatePass({translateFn,
  // continueOnError}), utilisée par les 2 modes :
  //   - FILL (bouton « Traduire ») : translateFn = autoTranslateEmptyLangs
  //     (ne remplit QUE les langues vides, jamais d'écrasement — règle L6),
  //     continueOnError:false → throw au 1er échec (comportement historique
  //     STRICTEMENT identique, messages inchangés).
  //   - OVERWRITE (bouton « Re-traduire tout », M7) : translateFn =
  //     retranslateAllLangs (écrasement confirmé par modale ; anti-wipe B1 :
  //     échec d'une locale ⇒ valeur existante conservée), continueOnError:true
  //     → compteurs {retraduits, inchangés, échecs}, la passe continue.
  // INVARIANTS PRÉSERVÉS VERBATIM (pessimist B7) : (a) gardes jsonb-vide FAQ,
  // (b) garde welcome_subtitle vide, (c) ordre modules→FAQ→config, (d) garde
  // fr-vide ⇒ cible intacte (portée par les 2 helpers de traduction).
  async function runTranslatePass({ translateFn, continueOnError }) {
    let translated = 0;
    let complete = 0;
    let failures = 0;
    const headers = await getAuthHeaders();

    // Échec de traduction d'une locale (mode overwrite) : la valeur existante
    // est conservée par le helper (anti-wipe B1) → l'élément compte en échec,
    // sans bloquer le PATCH des locales réussies.
    const hasFailedLocale = (...trs) =>
      trs.some((tr) => (tr.failed?.length || 0) > 0);

    // 1. Modules (titre + contenu)
    for (const m of modules) {
      try {
        const [trTitle, trContent] = await Promise.all([
          translateFn({
            fr: m.title?.fr,
            en: m.title?.en,
            es: m.title?.es,
            context: "title",
          }),
          translateFn({
            fr: m.content?.fr,
            en: m.content?.en,
            es: m.content?.es,
            context: "content",
          }),
        ]);
        const itemFailed = hasFailedLocale(trTitle, trContent);
        const tCh = langChanged(m.title, trTitle);
        const cCh = langChanged(m.content, trContent);
        if (!tCh && !cCh) {
          if (itemFailed) failures += 1;
          else complete += 1;
          continue;
        }
        const body = { action: "livret_module_upsert", id: m.id };
        if (tCh) body.title = buildLangPatch(m.title, trTitle);
        if (cCh) body.content = buildLangPatch(m.content, trContent);
        const r = await fetch(
          "/api/beds24-mapping?action=livret_module_upsert",
          { method: "PATCH", headers, body: JSON.stringify(body) },
        );
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.message || j.error || `module ${m.id} HTTP ${r.status}`);
        }
        if (itemFailed) failures += 1;
        else translated += 1;
      } catch (e) {
        if (!continueOnError) throw e;
        console.error("runTranslatePass — module en échec :", e);
        failures += 1;
      }
    }

    // 2. FAQ (question + réponse)
    for (const f of faqs) {
      try {
        const [trQ, trA] = await Promise.all([
          translateFn({
            fr: f.question?.fr,
            en: f.question?.en,
            es: f.question?.es,
            context: "content",
          }),
          translateFn({
            fr: f.answer?.fr,
            en: f.answer?.en,
            es: f.answer?.es,
            context: "content",
          }),
        ]);
        const itemFailed = hasFailedLocale(trQ, trA);
        const qCh = langChanged(f.question, trQ);
        const aCh = langChanged(f.answer, trA);
        if (!qCh && !aCh) {
          if (itemFailed) failures += 1;
          else complete += 1;
          continue;
        }
        // PATCH FAQ PARTIEL : on n'envoie QUE le(s) champ(s) réellement
        // traduit(s) et non vide(s). Le handler rejette un JSONB vide ; ne JAMAIS
        // envoyer un champ vide (ex. réponse FR vide non traduite) — sinon 400
        // qui interromprait tout le rattrapage. (Garde miroir de welcome_subtitle.)
        const body = { action: "livret_faq_upsert", id: f.id };
        if (qCh) {
          const q = buildLangPatch(f.question, trQ);
          if (Object.keys(q).length > 0) body.question = q;
        }
        if (aCh) {
          const a = buildLangPatch(f.answer, trA);
          if (Object.keys(a).length > 0) body.answer = a;
        }
        if (!body.question && !body.answer) {
          if (itemFailed) failures += 1;
          else complete += 1;
          continue;
        }
        const r = await fetch("/api/beds24-mapping?action=livret_faq_upsert", {
          method: "PATCH",
          headers,
          body: JSON.stringify(body),
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.message || j.error || `faq ${f.id} HTTP ${r.status}`);
        }
        if (itemFailed) failures += 1;
        else translated += 1;
      } catch (e) {
        if (!continueOnError) throw e;
        console.error("runTranslatePass — FAQ en échec :", e);
        failures += 1;
      }
    }

    // 3. Config (titre + sous-titre welcome)
    if (config) {
      try {
        const [trT, trW] = await Promise.all([
          translateFn({
            fr: config.title?.fr,
            en: config.title?.en,
            es: config.title?.es,
            context: "title",
          }),
          translateFn({
            fr: config.welcome_subtitle?.fr,
            en: config.welcome_subtitle?.en,
            es: config.welcome_subtitle?.es,
            context: "content",
          }),
        ]);
        const itemFailed = hasFailedLocale(trT, trW);
        const tCh = langChanged(config.title, trT);
        const wCh = langChanged(config.welcome_subtitle, trW);
        if (!tCh && !wCh) {
          if (itemFailed) failures += 1;
          else complete += 1;
        } else {
          const body = { action: "livret_config_upsert", logement_id };
          if (tCh) body.title = buildLangPatch(config.title, trT);
          if (wCh) {
            const w = buildLangPatch(config.welcome_subtitle, trW);
            // welcome_subtitle JSONB vide refusé serveur → n'inclure que si rempli.
            if (Object.keys(w).length > 0) body.welcome_subtitle = w;
          }
          if (body.title || body.welcome_subtitle) {
            const r = await fetch(
              "/api/beds24-mapping?action=livret_config_upsert",
              { method: "PATCH", headers, body: JSON.stringify(body) },
            );
            if (!r.ok) {
              const j = await r.json().catch(() => ({}));
              throw new Error(j.message || j.error || `config HTTP ${r.status}`);
            }
            if (itemFailed) failures += 1;
            else translated += 1;
          } else if (itemFailed) {
            failures += 1;
          } else {
            complete += 1;
          }
        }
      } catch (e) {
        if (!continueOnError) throw e;
        console.error("runTranslatePass — config en échec :", e);
        failures += 1;
      }
    }

    return { translated, complete, failures };
  }

  // Mode FILL (bouton « Traduire ») — langues VIDES uniquement, comportement
  // historique #618 strictement identique (throw au 1er échec, messages
  // inchangés, jamais d'écrasement).
  async function handleBackfillTranslate() {
    if (backfilling) return;
    setBackfilling(true);
    setBackfillMsg("Traduction en cours…");
    setError(null);
    try {
      const { translated, complete } = await runTranslatePass({
        translateFn: autoTranslateEmptyLangs,
        continueOnError: false,
      });
      setBackfillMsg(
        `✅ ${translated} élément(s) traduit(s), ${complete} déjà complet(s).`,
      );
      await loadData();
    } catch (e) {
      setBackfillMsg(null);
      setError(`Rattrapage interrompu : ${e.message || e}`);
    } finally {
      setBackfilling(false);
    }
  }

  // ── M7 — Re-traduire TOUT le livret (écrasement, GO produit Dimitri) ──────
  // Déclenché UNIQUEMENT via la modale de confirmation (compte des champs
  // écrasés affiché — pessimist B3). Passe overwrite : continueOnError, la
  // passe va au bout, compteurs {retraduits, inchangés, échecs}, loadData()
  // final même en passe partielle. Anti-wipe B1 : un échec de locale conserve
  // la valeur existante (jamais de trou dans le jsonb PATCHé).
  // NOTE HONNÊTE (pessimist B2) : idempotence NON garantie — le cache de
  // livret_translate est par-lambda (cold start = cache froid), un re-run peut
  // re-patcher des traductions quasi identiques. Best-effort assumé, dit dans
  // le message final ; jamais promis.
  async function handleRetranslateAll() {
    if (backfilling) return;
    setBackfilling(true);
    setBackfillMsg("Re-traduction en cours…");
    setError(null);
    try {
      const { translated, complete, failures } = await runTranslatePass({
        translateFn: (args) => retranslateAllLangs({ ...args, getAuthHeaders }),
        continueOnError: true,
      });
      setBackfillMsg(
        failures > 0
          ? `⚠️ ${translated} élément(s) re-traduit(s), ${complete} inchangé(s), ${failures} échec(s) — les versions existantes des éléments en échec sont conservées (rien n'est effacé). Re-traduction best-effort : relancer peut re-patcher des traductions quasi identiques.`
          : `✅ ${translated} élément(s) re-traduit(s), ${complete} inchangé(s). Re-traduction best-effort : relancer peut re-patcher des traductions quasi identiques.`,
      );
      // loadData() final MÊME en passe partielle (failures > 0 ne throw pas).
      await loadData();
    } catch (e) {
      setBackfillMsg(null);
      setError(`Re-traduction interrompue : ${e.message || e}`);
    } finally {
      setBackfilling(false);
    }
  }

  // ── Création config livret (cas step='no_config') ─────────────────────
  async function handleCreateConfig() {
    try {
      const headers = await getAuthHeaders();
      // Slug auto = logement_id (a-z0-9.) — la validation côté serveur
      // accepte slug /^[a-z0-9-]+$/i, on retire le point.
      const slug = String(logement_id)
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, "-");
      const resp = await fetch(
        `/api/beds24-mapping?action=livret_config_upsert`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            action: "livret_config_upsert",
            logement_id,
            slug,
            title: { fr: log?.nom || `Livret ${logement_id}` },
          }),
        },
      );
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json.message || json.error || `HTTP ${resp.status}`);
      }
      // Recharge
      await loadData();
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  // ── CRUD module callbacks ─────────────────────────────────────────────
  async function handleModuleSave(payload, moduleId) {
    const headers = await getAuthHeaders();
    const isEdit = !!moduleId;
    const body = isEdit
      ? { action: "livret_module_upsert", id: moduleId, ...payload }
      : { action: "livret_module_upsert", ...payload };
    const resp = await fetch(
      `/api/beds24-mapping?action=livret_module_upsert`,
      {
        method: isEdit ? "PATCH" : "POST",
        headers,
        body: JSON.stringify(body),
      },
    );
    const json = await resp.json();
    if (!resp.ok) {
      throw new Error(json.message || json.error || `HTTP ${resp.status}`);
    }
    await loadData();
  }

  async function handleModuleDelete(mod) {
    // Q-D8=A : hard delete + window.confirm
    const title = mod?.title?.fr || mod?.title?.en || "(sans titre)";
    if (
      !window.confirm(
        `Supprimer définitivement le module "${title}" ?\n\nCette action est irréversible.`,
      )
    ) {
      return;
    }
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `/api/beds24-mapping?action=livret_module_upsert`,
        {
          method: "DELETE",
          headers,
          body: JSON.stringify({
            action: "livret_module_upsert",
            id: mod.id,
          }),
        },
      );
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json.message || json.error || `HTTP ${resp.status}`);
      }
      await loadData();
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  async function handleModuleToggle(id, enabled) {
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `/api/beds24-mapping?action=livret_module_upsert`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify({
            action: "livret_module_upsert",
            id,
            enabled,
          }),
        },
      );
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json.message || json.error || `HTTP ${resp.status}`);
      }
      await loadData();
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  // Sprint A.9.4 PR-A93-2 (ADR-151) — Pré-remplir 9 appareils standards.
  async function handlePrefillAppareils() {
    setError(null);
    try {
      const resp = await fetch(
        "/api/beds24-mapping?action=livret_prefill_appareils",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ logement_id }),
        },
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(json.message || json.error || `HTTP ${resp.status}`);
      }
      await loadData();
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  // ── DnD handlers centralisés F-CRIT-3 ─────────────────────────────────
  //
  // Q-D Dimitri Option B : drag intra-catégorie uniquement.
  // Si over et active sont dans des colonnes différentes → reorderInCategory
  // retourne null → no-op silencieux (rejet inter-catégorie sans erreur UX).
  function handleDragStart() {
    isDraggingRef.current = true;
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    // Reset guard avant tout return (anti double-commit Strict Mode)
    isDraggingRef.current = false;

    if (!over || active.id === over.id) return;

    // ADR-171 (F-CRIT-3 pessimist) : itère Object.keys(localGrouped) dynamique
    // au lieu de LIVRET_CATEGORY_ORDER statique. Permet DnD sur catégories custom
    // créées via l'éditeur inline (sinon source/target=null silencieux pour cat custom).
    const dynamicOrder = Object.keys(localGrouped);
    // Détermine la catégorie source via lookup dans localGrouped
    let sourceCategory = null;
    for (const cat of dynamicOrder) {
      const list = localGrouped[cat] || [];
      if (list.some((m) => m.id === active.id)) {
        sourceCategory = cat;
        break;
      }
    }
    if (!sourceCategory) return;

    // Détermine la catégorie cible (over.id peut être dans une autre colonne)
    let targetCategory = null;
    for (const cat of dynamicOrder) {
      const list = localGrouped[cat] || [];
      if (list.some((m) => m.id === over.id)) {
        targetCategory = cat;
        break;
      }
    }
    if (!targetCategory) return;

    // Option B : si catégorie source ≠ cible → REJET (drag intra uniquement)
    if (sourceCategory !== targetCategory) return;

    // Reorder intra-catégorie via helper pur (A.4 réutilisé)
    const newOrderedIds = reorderInCategory(
      modules,
      sourceCategory,
      active.id,
      over.id,
    );
    if (!newOrderedIds) return;

    // Optimistic update LOCAL : reordonner localGrouped pour cette catégorie
    setLocalGrouped((prev) => {
      const reordered = newOrderedIds
        .map((id) => prev[sourceCategory].find((m) => m.id === id))
        .filter(Boolean);
      return { ...prev, [sourceCategory]: reordered };
    });

    // PATCH backend async (loadData re-sync prop modules → useEffect → localGrouped)
    handleModuleReorder(sourceCategory, newOrderedIds);
  }

  function handleDragCancel() {
    isDraggingRef.current = false;
  }

  async function handleModuleReorder(category, orderedIds) {
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `/api/beds24-mapping?action=livret_module_upsert`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            action: "livret_module_upsert",
            action_sub: "reorder",
            logement_id,
            category,
            ordered_ids: orderedIds,
          }),
        },
      );
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json.message || json.error || `HTTP ${resp.status}`);
      }
      await loadData();
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  // ── ADR-171 — Catégories inline (handlers) ────────────────────────────

  function handleEditCategoryStart(slug) {
    if (categoryBusyId) return;
    setEditingCategoryId(slug);
  }

  function handleEditCategoryCancel() {
    setEditingCategoryId(null);
  }

  async function handleEditCategorySave(slug, patch) {
    const cat = categories.find((c) => c.slug === slug);
    if (!cat) return;
    setCategoryBusyId(cat.id);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `/api/beds24-mapping?action=livret_category_update`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            action: "livret_category_update",
            id: cat.id,
            logement_id,
            label_fr: patch.label_fr,
            label_en: patch.label_en,
            label_es: patch.label_es,
          }),
        },
      );
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json.message || json.error || `HTTP ${resp.status}`);
      }
      // Trou A pessimist : loadData() pour re-sync categories state + localGrouped
      await loadData();
      setEditingCategoryId(null);
      // Trou C pessimist : annonce a11y polite
      setCategoryAnnounce(`Catégorie ${patch.label_fr} renommée`);
      setTimeout(() => setCategoryAnnounce(""), 3000);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setCategoryBusyId(null);
    }
  }

  async function handleAutoTranslateCategory(slug, labelFr) {
    if (!labelFr) return null;
    try {
      const headers = await getAuthHeaders();
      // Lance EN + ES en parallèle (Promise.allSettled — failure-tolerant)
      const [enResp, esResp] = await Promise.allSettled([
        fetch(`/api/beds24-mapping?action=livret_translate`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            action: "livret_translate",
            source_text: labelFr,
            target_locale: "en",
            context: "category_label",
          }),
        }).then((r) => r.json()),
        fetch(`/api/beds24-mapping?action=livret_translate`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            action: "livret_translate",
            source_text: labelFr,
            target_locale: "es",
            context: "category_label",
          }),
        }).then((r) => r.json()),
      ]);
      const enResult =
        enResp.status === "fulfilled" && !enResp.value.fallback
          ? enResp.value.translated
          : null;
      const esResult =
        esResp.status === "fulfilled" && !esResp.value.fallback
          ? esResp.value.translated
          : null;
      return { label_en: enResult, label_es: esResult };
    } catch {
      return null; // fallback gracieux : EN/ES restent éditables manuellement
    }
  }

  async function handleCreateCategory({ slug, label_fr, label_en, label_es }) {
    const headers = await getAuthHeaders();
    const resp = await fetch(
      `/api/beds24-mapping?action=livret_category_create`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          action: "livret_category_create",
          logement_id,
          slug,
          label_fr,
          label_en,
          label_es,
        }),
      },
    );
    const json = await resp.json();
    if (!resp.ok) {
      const err = new Error(json.message || json.error || `HTTP ${resp.status}`);
      err.status = resp.status;
      err.error = json.error;
      throw err;
    }
    await loadData();
    setCreatingCategoryModal(false);
    setCategoryAnnounce(`Catégorie ${label_fr} créée`);
    setTimeout(() => setCategoryAnnounce(""), 3000);
  }

  async function handleDeleteCategory(cat) {
    if (!cat || cat.is_system) return;
    const cnt = (localGrouped[cat.slug] || []).length;
    if (cnt > 0) {
      window.alert(
        `Impossible : ${cnt} module(s) sont attaché(s) à "${cat.label_fr}". Déplacez-les ou supprimez-les d'abord.`,
      );
      return;
    }
    if (
      !window.confirm(
        `Supprimer la catégorie "${cat.label_fr}" ?\nCette action est irréversible.`,
      )
    )
      return;
    setCategoryBusyId(cat.id);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `/api/beds24-mapping?action=livret_category_delete`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            action: "livret_category_delete",
            id: cat.id,
            logement_id,
          }),
        },
      );
      const json = await resp.json();
      if (!resp.ok) {
        if (json.error === "delete_blocked_modules_present") {
          window.alert(
            `Impossible : ${json.modules_count} module(s) sont encore attaché(s) à cette catégorie.`,
          );
          return;
        }
        throw new Error(json.message || json.error || `HTTP ${resp.status}`);
      }
      await loadData();
      setCategoryAnnounce(`Catégorie ${cat.label_fr} supprimée`);
      setTimeout(() => setCategoryAnnounce(""), 3000);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setCategoryBusyId(null);
    }
  }

  // ── Config save callback ──────────────────────────────────────────────
  async function handleConfigSave(patch) {
    const headers = await getAuthHeaders();
    const resp = await fetch(
      `/api/beds24-mapping?action=livret_config_upsert`,
      {
        method: "PATCH",
        headers,
        body: JSON.stringify({ action: "livret_config_upsert", ...patch }),
      },
    );
    const json = await resp.json();
    if (!resp.ok) {
      throw new Error(json.message || json.error || `HTTP ${resp.status}`);
    }
    await loadData();
  }

  async function handlePublishToggle(nextPublished) {
    await handleConfigSave({ logement_id, published: nextPublished });
  }

  // ── Plein écran : ouvre l'aperçu voyageur en overlay (réutilise previewUrl,
  // déjà construit proactivement ; fallback fetch si pas encore disponible). ──
  async function handleOpenPreview() {
    let url = previewUrl;
    if (!url) url = await fetchPreviewUrl();
    if (url) setFullscreenOpen(true);
  }

  // ── Rendering ─────────────────────────────────────────────────────────

  if (!logement_id) {
    return (
      <div style={{ padding: 16, color: DS.danger, fontSize: 12, fontFamily: DS.fontBody }}>
        Pas de logement_id (log.id) — composant inutilisable
      </div>
    );
  }

  if (step === "loading") {
    return (
      <div style={{ padding: 16, fontSize: 12, color: DS.textMuted, fontFamily: DS.fontBody }}>
        Chargement des modules livret…
      </div>
    );
  }

  if (step === "error") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          padding: 16,
          background: DS.dangerBg,
          border: `0.5px solid ${DS.danger}`,
          borderRadius: 8,
          color: DS.dangerText,
          fontSize: 12,
          fontFamily: DS.fontBody,
        }}
      >
        <AlertTriangle size={15} strokeWidth={1.8} style={{ flex: "0 0 auto" }} />
        <span style={{ flex: 1 }}>{error}</span>
        <button
          type="button"
          onClick={loadData}
          className="fbz-press"
          style={{
            padding: "5px 11px",
            background: DS.card,
            color: DS.marine,
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: DS.radiusControl,
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 600,
            fontFamily: DS.fontBody,
          }}
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (step === "no_config") {
    return (
      <div
        style={{
          padding: 20,
          background: DS.warningBg,
          border: `0.5px solid #f1d9ac`,
          borderLeft: `3px solid ${DS.warning}`,
          borderRadius: 10,
          color: DS.warningText,
          fontSize: 13,
          lineHeight: 1.6,
          fontFamily: DS.fontBody,
        }}
      >
        <strong>Aucun livret configuré</strong> pour ce logement.
        <br />
        Cliquer ci-dessous pour créer un livret par défaut (vous pourrez ensuite
        ajouter des modules et personnaliser).
        {error && (
          <div
            style={{
              marginTop: 10,
              padding: "8px 12px",
              background: DS.dangerBg,
              border: `0.5px solid ${DS.danger}`,
              borderRadius: 6,
              color: DS.dangerText,
              fontSize: 12,
            }}
          >
            Erreur création livret : {error}
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={handleCreateConfig}
            className="fbz-press"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "10px 18px",
              background: DS.corail,
              color: "#fff",
              border: "none",
              borderRadius: DS.radiusControl,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            <Rocket size={15} strokeWidth={1.8} /> Créer le livret
          </button>
        </div>
      </div>
    );
  }

  // step === 'ready' — utilise localGrouped (state optimiste F-CRIT-3)
  // L3 (maquette #3) : 2 colonnes — ÉDITEUR (accordéons tintés) + APERÇU (téléphone).
  const isPublished = config?.published === true;
  const totalModules = modules.length;
  const enabledModulesCount = modules.filter((m) => m.enabled !== false).length;
  const catCount = categoryOrder.length;
  const faqCount = faqs.length;
  const logementName =
    log?.nom || config?.title?.fr || config?.title?.en || config?.slug || "Logement";

  return (
    <div style={{ fontFamily: DS.fontBody }}>
      {isLocalhost && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            marginBottom: 14,
            padding: "8px 12px",
            background: DS.dangerBg,
            border: `0.5px solid ${DS.danger}`,
            borderRadius: 8,
            color: DS.dangerText,
            fontSize: 11,
            fontWeight: 700,
            textAlign: "center",
          }}
        >
          <AlertTriangle size={14} strokeWidth={1.8} /> MODE LOCALHOST — base de données PROD (toute modif est
          immédiatement visible voyageur)
        </div>
      )}

      {/* Bandeau statut/publier — pleine largeur au-dessus des 2 colonnes. */}
      <LivretPublishToggle
        config={config}
        modules={modules}
        onPublish={handlePublishToggle}
      />

      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            padding: 10,
            background: DS.dangerBg,
            border: `0.5px solid ${DS.danger}`,
            borderRadius: 8,
            color: DS.dangerText,
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          <AlertTriangle size={15} strokeWidth={1.8} style={{ flex: "0 0 auto" }} /> {error}
        </div>
      )}

      {/* Résumé du rattrapage traduction (N traduits, M déjà complets). */}
      {backfillMsg && (
        <div
          style={{
            padding: 10,
            background: DS.cielSoft,
            border: `0.5px solid ${DS.ciel}`,
            borderRadius: 8,
            color: DS.marineDeep,
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          {backfillMsg}
        </div>
      )}

      {/* ── 2 COLONNES : ÉDITEUR (gauche) + APERÇU TÉLÉPHONE (droite) ───────
          flex-wrap : à < ~860px les colonnes s'empilent (aperçu sous l'éditeur).
          Aucune minWidth bloquante → l'aperçu reste lisible jusqu'à 375px. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
        {/* COLONNE GAUCHE — ÉDITEUR DU LIVRET (accordéons tintés) */}
        <div style={{ flex: "1 1 520px" }}>
          <div
            style={{
              fontFamily: DS.fontDisplay,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: DS.textSecondary,
              marginBottom: 10,
            }}
          >
            Éditeur du livret
          </div>

          {/* APPARENCE & WIDGETS (ardoise) — configuration + localisation/météo + GYG */}
          <AccordionSection
            tintBg={DS.catArdoiseBg}
            tintIco={DS.catArdoiseIco}
            Icon={Palette}
            title="Apparence & widgets"
            chips={[
              isPublished
                ? { label: "Visible voyageur", tone: "on" }
                : { label: "Masqué", tone: "off" },
            ]}
            open={openSections.apparence}
            onToggle={() => toggleSection("apparence")}
          >
            <LivretConfigEditor
              config={config}
              logement_id={logement_id}
              onSave={handleConfigSave}
              lang={activeLang}
            />
          </AccordionSection>

          {/* MODULES DE CONTENU (ciel) — catégories système + modules + ajout */}
          <AccordionSection
            tintBg={DS.catCielBg}
            tintIco={DS.catCielIco}
            Icon={Box}
            title="Modules de contenu"
            chips={[
              { label: `${enabledModulesCount}/${totalModules} actifs`, tone: "info" },
              { label: `${catCount} catégories`, tone: "neutral" },
            ]}
            open={openSections.modules}
            onToggle={() => toggleSection("modules")}
          >
            {/* L3 — « Récupérer depuis [logement] » : copie de modules d'un autre
                logement (ajout, jamais d'écrasement). */}
            {copyCandidates.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <button
                  type="button"
                  onClick={() => setCopyModalMode("modules")}
                  className="fbz-press"
                  style={copyFromBtnStyle}
                >
                  <Download size={14} strokeWidth={1.8} /> Récupérer des modules depuis un autre logement
                </button>
              </div>
            )}
            {/* ADR-171 — annonce a11y polite (Trou C pessimist) */}
            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              data-testid="livret-category-announce"
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
            >
              {categoryAnnounce}
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                  gap: 14,
                }}
              >
                {/* ADR-171 — itération dynamique sur categories[] (ou fallback statique).
                    F-CRIT-1 mitigation : catégories custom rendues automatiquement. */}
                {categoryOrder.map((slug) => {
                  const catData = categories.find((c) => c.slug === slug);
                  return (
                    <LivretCategoryColumn
                      key={slug}
                      category={slug}
                      categoryData={catData}
                      modules={localGrouped[slug] || []}
                      activeLang={activeLang}
                      onModuleCreate={(c) => setCreatingCategory(c)}
                      onModuleEdit={(m) => setEditingModule(m)}
                      onModuleDelete={handleModuleDelete}
                      onModuleToggle={handleModuleToggle}
                      onPrefillAppareils={
                        slug === "guide_appareils"
                          ? handlePrefillAppareils
                          : undefined
                      }
                      isEditingCategory={editingCategoryId === slug}
                      categoryBusy={
                        catData ? categoryBusyId === catData.id : false
                      }
                      onEditCategoryStart={handleEditCategoryStart}
                      onEditCategorySave={handleEditCategorySave}
                      onEditCategoryCancel={handleEditCategoryCancel}
                      onAutoTranslate={handleAutoTranslateCategory}
                      onDeleteCategory={handleDeleteCategory}
                    />
                  );
                })}

                {/* ADR-171 — colonne fantôme "+ Nouvelle catégorie" en fin de grille.
                    Héritage auto-fill garantit wrap naturel sur mobile (M-3 pessimist). */}
                <div
                  style={{
                    background: DS.card,
                    borderRadius: DS.radiusCard,
                    padding: 14,
                    marginBottom: 14,
                    border: `1.5px dashed ${DS.borderHover}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 100,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setCreatingCategoryModal(true)}
                    className="fbz-press"
                    style={{
                      width: "100%",
                      padding: "16px 14px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 7,
                      background: "transparent",
                      border: "none",
                      color: DS.marine,
                      fontFamily: DS.fontBody,
                      fontSize: 13.5,
                      fontWeight: 600,
                      cursor: "pointer",
                      minHeight: 44,
                    }}
                    data-testid="livret-category-add-button"
                  >
                    <Plus size={16} strokeWidth={2} /> Nouvelle catégorie
                  </button>
                </div>
              </div>
            </DndContext>

            {creatingCategoryModal && (
              <CreateCategoryModal
                existingSlugs={categories.map((c) => c.slug)}
                onClose={() => setCreatingCategoryModal(false)}
                onSubmit={handleCreateCategory}
              />
            )}
          </AccordionSection>

          {/* SECTIONS AUTOMATIQUES IA (indigo) — assistance / alentours / marches */}
          <AccordionSection
            tintBg={DS.catIndigoBg}
            tintIco={DS.catIndigoIco}
            Icon={Sparkles}
            // MINOR-1 — le leaf LivretSectionsAutoEditor affiche déjà son propre
            // h3 « Sections automatiques IA » : titre d'accordéon raccourci pour
            // éviter le doublon empilé (teinte indigo + icône Sparkles portent
            // l'identité « IA » au niveau de la barre).
            title="Sections automatiques"
            chips={[{ label: "Automatique", tone: "indigo" }]}
            open={openSections.sections_auto}
            onToggle={() => toggleSection("sections_auto")}
          >
            <LivretSectionsAutoEditor logement_id={logement_id} />
          </AccordionSection>

          {/* FAQ (ciel) */}
          <AccordionSection
            tintBg={DS.catCielBg}
            tintIco={DS.catCielIco}
            Icon={HelpCircle}
            title="FAQ"
            chips={[{ label: `${faqCount} question(s)`, tone: "info" }]}
            open={openSections.faq}
            onToggle={() => toggleSection("faq")}
          >
            {/* L3 — « Récupérer depuis [logement] » : copie de FAQ. */}
            {copyCandidates.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <button
                  type="button"
                  onClick={() => setCopyModalMode("faqs")}
                  className="fbz-press"
                  style={copyFromBtnStyle}
                >
                  <Download size={14} strokeWidth={1.8} /> Récupérer des FAQ depuis un autre logement
                </button>
              </div>
            )}
            <LivretFaqEditor
              logement_id={logement_id}
              faqs={faqs}
              onReload={loadData}
              lang={activeLang}
            />
          </AccordionSection>

          {/* INFOS PRATIQUES (ardoise) — google review / website / pointeur réseaux.
              L7 : Configuration avancée (fuseau horaire) supprimée (champ mort). */}
          <AccordionSection
            tintBg={DS.catArdoiseBg}
            tintIco={DS.catArdoiseIco}
            Icon={Rss}
            // MINOR-1 — le leaf LivretInfosEditor affiche déjà son propre heading
            // « Infos pratiques » : titre d'accordéon différencié (le leaf édite
            // google review / website / pointeur réseaux = des liens).
            title="Infos pratiques & liens"
            chips={[]}
            open={openSections.infos}
            onToggle={() => toggleSection("infos")}
          >
            <LivretInfosEditor
              config={config}
              logement_id={logement_id}
              onReload={loadData}
            />
          </AccordionSection>
        </div>

        {/* COLONNE DROITE — APERÇU VOYAGEUR RÉEL INLINE (iframe gelée ADR-149).
            Le cadre « téléphone » EST désormais l'iframe du rendu voyageur réel
            (LivretPreviewPanel inline) — plus aucune enveloppe décorative. La
            route /livret/r/:token ne rend le voyageur que si le livret est publié
            (cf. handleLivretAdminPreviewToken) → sinon placeholder « publiez ». */}
        <div style={{ flex: "1 1 300px", position: "sticky", top: 12 }}>
          {isPublished && previewUrl ? (
            /* B6 — onReload : « Recharger » re-fetch le token-URL AVANT le
               key bump (try/finally côté panel : le bump s'exécute toujours). */
            <LivretPreviewPanel
              previewUrl={previewUrl}
              inline
              onReload={fetchPreviewUrl}
            />
          ) : (
            <PreviewPlaceholder
              logementName={logementName}
              published={isPublished}
              loading={previewLoading}
              error={previewError}
              onRetry={fetchPreviewUrl}
            />
          )}

          {/* Sous l'aperçu : langue + Plein écran + QR/PDF + Traduire. */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginTop: 12,
              alignItems: "center",
            }}
          >
            {/* Sélecteur de langue (FR/EN/ES en pilules texte, pas d'emoji-
                drapeau). Un seul état (activeLang), rendu ici ET dans les modals.
                L6 : pilote l'aperçu + la carte Configuration + l'édition Modules/FAQ. */}
            <div
              style={{
                display: "flex",
                gap: 4,
                background: DS.card,
                borderRadius: DS.radiusControl,
                padding: 4,
                border: `0.5px solid ${DS.border}`,
              }}
              title="Langue affichée (aperçu, Configuration, Modules, FAQ)"
            >
              {["fr", "en", "es"].map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => handleSetLang(lang)}
                  aria-pressed={activeLang === lang}
                  style={{
                    padding: "6px 12px",
                    border: "none",
                    borderRadius: 6,
                    background: activeLang === lang ? DS.marine : "transparent",
                    color: activeLang === lang ? "#fff" : DS.textSecondary,
                    fontWeight: activeLang === lang ? 700 : 600,
                    fontSize: 12,
                    cursor: "pointer",
                    fontFamily: DS.fontBody,
                  }}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Plein écran = aperçu voyageur via iframe (rendu gelé ADR-149). */}
            <button
              type="button"
              onClick={handleOpenPreview}
              disabled={previewLoading || !isPublished}
              title={
                !isPublished
                  ? "Publier le livret pour activer l'aperçu plein écran"
                  : "Ouvrir l'aperçu voyageur en plein écran"
              }
              className="fbz-press"
              style={{
                ...previewBtnStyle,
                opacity: isPublished && !previewLoading ? 1 : 0.5,
                cursor:
                  isPublished && !previewLoading ? "pointer" : "not-allowed",
              }}
            >
              <Maximize2 size={14} strokeWidth={1.8} />
              {previewLoading ? "Chargement…" : "Plein écran"}
            </button>

            <LivretQrDownloadButton
              slug={config?.slug}
              title={config?.title?.fr || config?.title?.en || config?.slug}
              logement_id={logement_id}
            />

            {/* Rattrapage : traduit FR → EN/ES les langues VIDES de tout le
                livret (modules + FAQ + config). Jamais d'écrasement (helper L6). */}
            <button
              type="button"
              onClick={handleBackfillTranslate}
              disabled={backfilling}
              title="Traduire automatiquement (FR → EN + ES) les langues encore vides des modules, FAQ et de la configuration. N'écrase aucune traduction existante."
              className="fbz-press"
              style={{ ...previewBtnStyle, opacity: backfilling ? 0.5 : 1, cursor: backfilling ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
            >
              <Languages size={14} strokeWidth={1.8} />
              {backfilling ? "Traduction…" : "Traduire"}
            </button>

            {/* M7 — Re-traduction avec ÉCRASEMENT : ouvre une modale de
                confirmation (jamais d'écrasement direct — GO produit Dimitri
                « confirmation explicite avant »). Verrou mutuel `backfilling`
                partagé avec « Traduire » (les 2 passes ne coexistent jamais). */}
            <button
              type="button"
              onClick={() =>
                setRetranslateCount(
                  countOverwritableLangs(modules, faqs, config),
                )
              }
              disabled={backfilling}
              title="Re-traduire TOUT le livret (FR → EN + ES) en remplaçant les traductions existantes, y compris les corrections manuelles. Une confirmation est demandée avant. Le français n'est jamais modifié."
              className="fbz-press"
              style={{ ...previewBtnStyle, opacity: backfilling ? 0.5 : 1, cursor: backfilling ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
            >
              <Languages size={14} strokeWidth={1.8} />
              Re-traduire tout
            </button>
          </div>
        </div>
      </div>

      {/* Modal CRUD */}
      {(editingModule || creatingCategory) && (
        <LivretModuleEditor
          module={editingModule}
          category={creatingCategory || editingModule?.category}
          logement_id={logement_id}
          onSave={handleModuleSave}
          onClose={() => {
            setEditingModule(null);
            setCreatingCategory(null);
            // Sprint A.9.1 PR-A91-2 (ADR-128) : reload pour ré-aspirer
            // les module_photos modifiées via LivretModulePhotoCover.
            loadData();
          }}
          // Sprint A.9.1 PR-A91-2 (ADR-128) F-CRIT mitigation :
          // passer currentPhoto[0] (1 seule photo cover par module)
          currentPhoto={modulePhotos[editingModule?.id]?.[0] ?? null}
          lang={activeLang}
          onLangChange={handleSetLang}
        />
      )}

      {/* Preview iframe — PLEIN ÉCRAN (overlay), déclenché par « Plein écran ».
          L'aperçu inline (colonne droite) réutilise le même previewUrl. */}
      {fullscreenOpen && previewUrl && (
        <LivretPreviewPanel
          previewUrl={previewUrl}
          onClose={() => setFullscreenOpen(false)}
          onReload={fetchPreviewUrl}
        />
      )}

      {/* M7 — modale de confirmation « Re-traduire tout » (compte affiché). */}
      {retranslateCount !== null && (
        <RetranslateConfirmModal
          count={retranslateCount}
          onClose={() => setRetranslateCount(null)}
          onConfirm={() => {
            setRetranslateCount(null);
            handleRetranslateAll();
          }}
        />
      )}

      {/* L3 — modal « Récupérer depuis [logement] » (modules OU FAQ). */}
      {copyModalMode && (
        <LivretCopyFromModal
          mode={copyModalMode}
          targetLogementId={logement_id}
          candidates={copyCandidates}
          onClose={() => setCopyModalMode(null)}
          onCopied={async (res) => {
            const n =
              copyModalMode === "modules"
                ? res?.copied_modules
                : res?.copied_faqs;
            const skipped = res?.skipped_photos
              ? ` (${res.skipped_photos} module(s) avec photos non copiées)`
              : "";
            setBackfillMsg(`✅ ${n || 0} élément(s) copié(s)${skipped}.`);
            await loadData();
          }}
        />
      )}
    </div>
  );
}

// ── AccordionSection (L3, maquette #3) ──────────────────────────────────────
// Section repliable à teinte-catégorie : pastille d'icône tintée + titre +
// chips d'état (read-only) + chevron. Le contenu n'est monté que si `open`.
// Habillage pur — aucune logique métier (pilotée par le parent).
function AccordionSection({ tintBg, tintIco, Icon, title, chips, open, onToggle, children }) {
  const list = Array.isArray(chips) ? chips : [];
  return (
    <div
      className="fbz-card"
      style={{
        background: DS.card,
        borderRadius: DS.radiusCard,
        border: `0.5px solid ${DS.border}`,
        marginBottom: 12,
        overflow: "hidden",
        fontFamily: DS.fontBody,
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 11,
          padding: "12px 14px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          fontFamily: DS.fontBody,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: "inline-flex",
            width: 30,
            height: 30,
            borderRadius: 9,
            background: tintBg,
            color: tintIco,
            alignItems: "center",
            justifyContent: "center",
            flex: "0 0 auto",
          }}
        >
          <Icon size={16} strokeWidth={1.8} />
        </span>
        <span
          style={{
            fontFamily: DS.fontDisplay,
            fontSize: 14.5,
            fontWeight: 600,
            color: DS.marineDark,
            flex: "0 1 auto",
          }}
        >
          {title}
        </span>
        <span style={{ display: "flex", flexWrap: "wrap", gap: 6, flex: 1 }}>
          {list.map((c, i) => (
            <StateChip key={i} label={c.label} tone={c.tone} />
          ))}
        </span>
        <ChevronDown
          size={18}
          strokeWidth={1.8}
          style={{
            flex: "0 0 auto",
            color: DS.textSecondary,
            transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            transition: `transform ${DS.durBase}`,
          }}
        />
      </button>
      {open && (
        <div style={{ padding: "4px 14px 16px" }}>{children}</div>
      )}
    </div>
  );
}

// Chip d'état (read-only) — tons sémantiques DA.
function StateChip({ label, tone }) {
  const map = {
    on: { bg: DS.successBg, fg: DS.successText },
    off: { bg: DS.warningBg, fg: DS.warningText },
    info: { bg: DS.cielSoft, fg: DS.marineDeep },
    neutral: { bg: DS.catArdoiseBg, fg: DS.textSecondary },
    indigo: { bg: DS.catIndigoBg, fg: DS.catIndigoIco },
  };
  const c = map[tone] || map.neutral;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 9px",
        borderRadius: 999,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: 0.2,
        background: c.bg,
        color: c.fg,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// ── PreviewPlaceholder ──────────────────────────────────────────────────────
// Affiché à la place de l'aperçu inline quand le livret n'est pas encore publié
// (la route voyageur /livret/r/:token ne rend le contenu que si published) ou
// pendant le chargement du token. Habillage pur — aucune donnée voyageur, aucun
// contenu réel (ce n'est PAS un faux rendu). Le « Plein écran » reste gaté sur
// published (même contrainte).
// B6 — props error + onRetry : si le fetch du token échoue (published, pas en
// chargement), on affiche « Aperçu indisponible : {error} » + « Réessayer »
// (disabled pendant previewLoading — anti spam-clic, condition pessimist B5).
function PreviewPlaceholder({ logementName, published, loading, error, onRetry }) {
  const showError = published && !loading && !!error;
  return (
    <div
      style={{
        width: "100%",
        maxWidth: 360,
        margin: "0 auto",
        borderRadius: 26,
        background: DS.card,
        border: `1px dashed ${DS.borderHover}`,
        boxShadow: "0 8px 28px rgba(20,51,80,0.08)",
        padding: 28,
        textAlign: "center",
        fontFamily: DS.fontBody,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          width: 44,
          height: 44,
          borderRadius: 12,
          background: DS.cielSoft,
          color: DS.marine,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Smartphone size={22} strokeWidth={1.8} />
      </span>
      <div
        style={{
          fontFamily: DS.fontDisplay,
          fontSize: 15,
          fontWeight: 600,
          color: DS.marineDark,
          marginTop: 12,
        }}
      >
        Aperçu voyageur
      </div>
      {!showError && (
        <div style={{ fontSize: 12.5, color: DS.textSecondary, marginTop: 8, lineHeight: 1.5 }}>
          {loading
            ? "Chargement de l'aperçu en direct…"
            : published
              ? "Préparation de l'aperçu en direct…"
              : `Publiez le livret pour afficher l'aperçu en direct de « ${logementName} » tel que le voyageur le verra.`}
        </div>
      )}
      {showError && (
        <>
          <div
            style={{
              marginTop: 8,
              fontSize: 12.5,
              color: DS.dangerText,
              lineHeight: 1.5,
            }}
          >
            Aperçu indisponible : {error}
          </div>
          <button
            type="button"
            onClick={onRetry}
            disabled={loading}
            className="fbz-press"
            style={{
              ...previewBtnStyle,
              marginTop: 12,
              opacity: loading ? 0.5 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            <RotateCcw size={14} strokeWidth={1.8} /> Réessayer
          </button>
        </>
      )}
    </div>
  );
}

// Boutons sobres sous l'aperçu (DA §8 ter) — IDENTIQUES (blanc-marine),
// JAMAIS une barre arc-en-ciel. Plein écran / QR / Traduire partagent ce gabarit.
const previewBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "8px 14px",
  background: DS.card,
  color: DS.marine,
  border: `0.5px solid ${DS.borderHover}`,
  borderRadius: DS.radiusControl,
  fontSize: 12,
  fontWeight: 600,
  fontFamily: DS.fontBody,
};

// L3 — style bouton « Récupérer depuis » (onglets Modules + FAQ).
const copyFromBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "8px 14px",
  background: "transparent",
  color: DS.textSecondary,
  border: `1.5px dashed ${DS.borderHover}`,
  borderRadius: DS.radiusControl,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: DS.fontBody,
};
